import { baseApi } from './base.api';
import { METRIC_LABELS } from 'src/pages/AnalyticsPage/constants';
import moment from 'moment';
import { API_VERSION, PATH_ANALYTICS_API } from 'src/constants/api-routes';
import { formatDuration } from 'src/utils/formatDuration';

export interface CoAgentAnalyticsRequest {
  metrics: string[];
  date_range: string;
  // A moment duration unit such as 'minutes' or 'hours'.
  time_axis: string;
  // Only needed for a 'day' range, to pre-fill every date between them.
  start_date?: string;
  end_date?: string;
  project_id?: number | null;
  agents?: unknown[];
  data_visualization?: string;
}

// A headline metric and its change against the previous period.
interface MetricMeta {
  value: number;
  diff?: number | null;
}

interface DurationMeta extends MetricMeta {
  formattedValue?: string;
}

export interface CoAgentAnalyticsResponse {
  meta: {
    first_response_time: DurationMeta;
    response_time: DurationMeta;
    resolved_with_ai?: MetricMeta;
    accuracy?: MetricMeta;
    bad_suggestions?: MetricMeta;
    [key: string]: unknown;
  };
  // metric -> (crm user id | 'subdomain') -> date -> value
  data: Record<string, Record<string, Record<string, number>>>;
}

// One chart row: the date plus a value per metric (team-wide) or per crm user id.
export interface CoAgentAnalyticsRow {
  date: string;
  [key: string]: string | number | Record<string, number>;
}

export interface ParsedCoAgentAnalytics {
  request: CoAgentAnalyticsRequest;
  isByUser: boolean;
  meta: CoAgentAnalyticsResponse['meta'];
  data: CoAgentAnalyticsRow[];
}

export interface CoAgentAnalytics {
  response: CoAgentAnalyticsResponse;
  parsedResponse: ParsedCoAgentAnalytics;
}

const getDateRange = (firstDate: string, lastDate: string): CoAgentAnalyticsRow[] => {
  if (moment(firstDate, 'YYYY-MM-DD').isSame(moment(lastDate, 'YYYY-MM-DD'), 'day'))
    return [{ date: moment(lastDate).format('ll') }];
  const date = moment(firstDate);
  const dates = [{ date: date.format('ll') }];
  do {
    date.add(1, 'day');
    dates.push({ date: date.format('ll') });
  } while (date.isBefore(lastDate));
  return dates;
};

const sortByDate = (parsedResponse: ParsedCoAgentAnalytics) => {
  parsedResponse.data.sort((item, item2) => {
    const isRange = item.date.includes(' - ');
    const first = isRange ? item.date.split(' - ')[0] : item.date;
    const second = isRange ? item2.date.split(' - ')[0] : item2.date;

    return moment(first, 'll').diff(moment(second, 'll'));
  });
};

const formatDate = (date: string) => {
  const isRange = date.includes(' - ');

  if (isRange) {
    return date
      .split(' - ')
      .map((datePart) => moment(datePart).format('ll'))
      .join(' - ');
  }

  // Hourly points carry a time; daily points show the date only.
  return moment(date).creationData().format === 'YYYY-MM-DD HH:mm'
    ? moment(date).format('MMM DD, hh:mm A')
    : moment(date).format('ll');
};

const parseByMetric = (
  metric: string,
  dataKey: string,
  response: CoAgentAnalyticsResponse,
  result: ParsedCoAgentAnalytics,
) => {
  const data = response.data[dataKey];

  if (result.isByUser) {
    Object.keys(data).forEach((crmUserId) => {
      Object.keys(data[crmUserId]).forEach((dateKey) => {
        const date = formatDate(dateKey);

        result.data = result.data || [];

        const dataItem = result.data.find((item) => {
          return item.date === date;
        });

        if (dataItem) {
          const userValues = dataItem[crmUserId] as Record<string, number> | undefined;

          dataItem[crmUserId] = userValues
            ? {
                ...userValues,
                [METRIC_LABELS[metric]]: data[crmUserId][dateKey] || 0,
              }
            : {
                [METRIC_LABELS[metric]]: data[crmUserId][dateKey] || 0,
              };
        } else {
          result.data.push({
            date,
            [crmUserId]: {
              [METRIC_LABELS[metric]]: data[crmUserId][dateKey] || 0,
            },
          });
        }
      });
    });
  } else {
    data.subdomain &&
      Object.keys(data.subdomain).forEach((key) => {
        const date = formatDate(key);
        const dataItem = result.data.find((item) => {
          return item.date === date;
        });

        if (dataItem) {
          dataItem[METRIC_LABELS[metric]] = data.subdomain[key] || 0;
        } else {
          result.data.push({
            date,
            [METRIC_LABELS[metric]]: data.subdomain[key] || 0,
          });
        }
      });
  }
};

const parseResponse = (
  request: CoAgentAnalyticsRequest,
  response: CoAgentAnalyticsResponse,
): ParsedCoAgentAnalytics => {
  const isByUser = !request.metrics.some((metric) => metric.includes('subdomain_'));
  const result: ParsedCoAgentAnalytics = {
    request,
    isByUser,
    meta: response.meta,
    data: request.date_range === 'day' ? getDateRange(request.start_date, request.end_date) : [],
  };

  result.meta.first_response_time.formattedValue = formatDuration(
    result.meta.first_response_time.value,
    request.time_axis as moment.unitOfTime.DurationConstructor,
    's',
  );

  result.meta.response_time.formattedValue = formatDuration(
    result.meta.response_time.value,
    request.time_axis as moment.unitOfTime.DurationConstructor,
    's',
  );

  request.metrics.forEach((metric) => {
    let dataKey = metric;

    dataKey = dataKey.replace('subdomain_', '');

    if (!response.data[dataKey]) {
      return;
    }

    parseByMetric(metric, dataKey, response, result);
  });

  return result;
};

export const coAgentAnalyticsService = baseApi.injectEndpoints({
  endpoints: (build) => ({
    fetchCoAgentAnalytics: build.mutation<CoAgentAnalytics, CoAgentAnalyticsRequest>({
      query: (params) => ({
        url: `${API_VERSION.V1}/${PATH_ANALYTICS_API.COAGENT}`,
        method: 'POST',
        body: params,
      }),
      transformResponse: (response: CoAgentAnalyticsResponse, _meta, arg) => {
        const parsedResponse = parseResponse(arg, response);

        sortByDate(parsedResponse);

        return { response, parsedResponse };
      },
    }),
  }),
});

export const { useFetchCoAgentAnalyticsMutation } = coAgentAnalyticsService;
